import {
  Box,
  Chip,
  Paper,
  Stack,
  alpha,
  Select,
  Button,
  MenuItem,
  useTheme,
  Typography,
  InputLabel,
  FormControl,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import ToneChip from './tone-chip';
import { renderFormattedDate } from 'utils';
import { TAKE_OPTIONS } from '../constants';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage, useIntl } from 'react-intl';
import { FreshdeskDashboardResponse } from 'library/types';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

type DashboardHeroProps = {
  dashboard: FreshdeskDashboardResponse | null;
  loading: boolean;
  take: number;
  onTakeChange: (take: number) => void;
  onRefresh: () => void;
};

const DashboardHero = ({ dashboard, loading, take, onTakeChange, onRefresh }: DashboardHeroProps) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2, md: 3 },
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.info.main, 0.045),
      }}>
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
          }}
        />
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between">
        <Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              display: 'grid',
              flexShrink: 0,
              placeItems: 'center',
              borderRadius: '8px',
              color: theme.palette.success.main,
              bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
              '& .MuiSvgIcon-root': { fontSize: 30 },
            }}>
            <SupportAgentIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.12 }}>
                <FormattedMessage id="freshdesk.title" />
              </Typography>
              <ToneChip tone="success" label={<FormattedMessage id="freshdesk.liveBadge" />} />
            </Stack>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
              <FormattedMessage id="freshdesk.subtitle" />
            </Typography>

            <Stack direction="row" flexWrap="wrap" gap={1.25} sx={{ mt: 1.5 }}>
              <Chip
                size="small"
                label={intl.formatMessage(
                  { id: 'freshdesk.reportDate' },
                  {
                    date: dashboard?.reportDate
                      ? renderFormattedDate(dashboard.reportDate)
                      : intl.formatMessage({ id: 'freshdesk.notAvailable' }),
                  }
                )}
                sx={{ borderRadius: '999px', fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={intl.formatMessage(
                  { id: 'freshdesk.currentAgent' },
                  { name: dashboard?.currentAgent?.name || intl.formatMessage({ id: 'freshdesk.notAvailable' }) }
                )}
                sx={{ borderRadius: '999px', fontWeight: 700 }}
              />
            </Stack>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ flexShrink: 0 }}>
          <FormControl size="small" sx={{ minWidth: 172 }}>
            <InputLabel id="freshdesk-take-label">
              <FormattedMessage id="freshdesk.takeLabel" />
            </InputLabel>
            <Select<number>
              labelId="freshdesk-take-label"
              value={take}
              label={intl.formatMessage({ id: 'freshdesk.takeLabel' })}
              onChange={(event) => onTakeChange(Number(event.target.value))}>
              {TAKE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {intl.formatMessage({ id: 'freshdesk.takeOption' }, { count: option })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <RefreshIcon />}
            disabled={loading}
            onClick={onRefresh}>
            <FormattedMessage id="freshdesk.refresh" />
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default DashboardHero;
